import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import ESignatureDialog from "@/components/ESignatureDialog";
import AuditTrailPanel from "@/components/AuditTrailPanel";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import ComplianceCoach from "@/components/ComplianceCoach";
import { FileText, Plus, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const STATES = ["Draft", "Review", "Approved", "Effective", "Obsolete"];
const STATE_COLORS = {
  Draft: "bg-slate-100 text-slate-700",
  Review: "bg-blue-50 text-blue-700",
  Approved: "bg-violet-50 text-violet-700",
  Effective: "bg-emerald-50 text-emerald-700",
  Obsolete: "bg-slate-200 text-slate-500 line-through",
};

export default function Documents() {
  const [list, setList] = useState([]);
  const [detail, setDetail] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", doc_type: "SOP", content: "" });
  const [esignOpen, setEsignOpen] = useState(false);
  const [pending, setPending] = useState(null);

  const load = async () => setList((await api.get("/documents")).data);
  useEffect(() => { load(); }, []);
  const loadDetail = async (id) => setDetail((await api.get(`/documents/${id}`)).data);

  const create = async () => {
    if (!form.title || !form.content) return toast.error("Title and content required");
    try {
      await api.post("/documents", form);
      toast.success("Document created (Draft)");
      setOpen(false);
      setForm({ title: "", doc_type: "SOP", content: "" });
      load();
    } catch (e) {
      // 403/500 already surfaced by the axios interceptor; catch stops the
      // unhandled-promise error from being silent for 4xx validation.
      const detail = e.response?.data?.detail;
      if (detail && e.response?.status !== 403 && e.response?.status < 500) {
        toast.error(typeof detail === "string" ? detail : "Create failed");
      }
    }
  };

  const requestTransition = (toState) => {
    setPending({ toState });
    setEsignOpen(true);
  };

  const onSigned = async (sig) => {
    const payload = { to_state: pending.toState, signature_id: sig.signature_id, reason: sig.reason };
    if (pending.toState === "Effective") payload.effective_date = new Date().toISOString().slice(0, 10);
    await api.post(`/documents/${detail.document_id}/transition`, payload);
    toast.success(`Moved to ${pending.toState}`);
    setPending(null);
    loadDetail(detail.document_id);
    load();
    setRefreshKey((k) => k + 1);
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="vq-overline mb-2">Document Control</div>
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900">Controlled Documents</h1>
            <p className="text-slate-600 mt-2">SOPs, Policies, Work Instructions, Records — with lifecycle states and e-signatures.</p>
          </div>
          <Button onClick={() => setOpen(true)} data-testid="doc-new-btn" className="h-11 bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
            <Plus className="w-4 h-4 mr-2" /> New Document
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 vq-card overflow-hidden">
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 vq-overline">Repository ({list.length})</div>
            <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto">
              {list.map((d) => (
                <button key={d.document_id} data-testid={`doc-row-${d.document_id}`}
                        onClick={() => loadDetail(d.document_id)}
                        className={`w-full text-left px-4 py-3 hover:bg-slate-50 ${detail?.document_id === d.document_id ? "bg-slate-100" : ""}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="w-3.5 h-3.5 text-slate-400" />
                    <span className="text-[10px] font-mono text-slate-500">{d.doc_type} • v{d.version}</span>
                    <span className={`ml-auto px-1.5 py-0.5 rounded text-[9px] font-mono ${STATE_COLORS[d.state]}`}>{d.state}</span>
                  </div>
                  <div className="font-semibold text-sm text-slate-900 truncate">{d.title}</div>
                </button>
              ))}
              {list.length === 0 && <div className="p-6 text-center text-sm text-slate-500">No documents.</div>}
            </div>
          </div>

          <div className="lg:col-span-3">
            {detail ? (
              <>
                <div className="vq-card p-6 mb-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="vq-overline font-mono">{detail.document_id} • v{detail.version}</div>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-mono ${STATE_COLORS[detail.state]}`}>{detail.state}</span>
                  </div>
                  <h2 className="font-display font-bold text-2xl text-slate-900">{detail.title}</h2>
                  <div className="text-xs font-mono text-slate-500 mt-1">
                    {detail.doc_type} • {detail.author_email} {detail.effective_date && `• Effective ${detail.effective_date}`}
                  </div>

                  <div className="flex items-center flex-wrap gap-2 my-4">
                    {STATES.map((s, i) => {
                      const idx = STATES.indexOf(detail.state);
                      return (
                        <div key={s} className="flex items-center gap-2">
                          <span data-testid={`doc-state-${s}`} className={`px-2 py-1 rounded text-[10px] font-mono ${
                            s === detail.state ? "bg-slate-900 text-white" : i < idx ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"
                          }`}>{s}</span>
                          {i < STATES.length - 1 && <ArrowRight className="w-3 h-3 text-slate-300" />}
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {detail.state === "Draft" && (
                      <Button onClick={() => requestTransition("Review")} data-testid="doc-review-btn" className="bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
                        <ShieldCheck className="w-4 h-4 mr-2" /> Submit for Review
                      </Button>
                    )}
                    {detail.state === "Review" && (
                      <Button onClick={() => requestTransition("Approved")} data-testid="doc-approve-btn" className="bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
                        <ShieldCheck className="w-4 h-4 mr-2" /> Approve
                      </Button>
                    )}
                    {detail.state === "Approved" && (
                      <Button onClick={() => requestTransition("Effective")} data-testid="doc-effective-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
                        <ShieldCheck className="w-4 h-4 mr-2" /> Make Effective
                      </Button>
                    )}
                    {detail.state === "Effective" && (
                      <Button onClick={() => requestTransition("Obsolete")} data-testid="doc-obsolete-btn" variant="outline" className="border-slate-300 font-semibold">
                        Mark Obsolete
                      </Button>
                    )}
                  </div>
                </div>

                <div className="vq-card p-6 mb-4">
                  <h3 className="font-display font-bold text-sm text-slate-900 mb-3">Content (v{detail.version})</h3>
                  <pre className="whitespace-pre-wrap text-sm text-slate-700 font-mono bg-slate-50 border border-slate-200 rounded p-4 max-h-64 overflow-y-auto">{detail.content}</pre>
                </div>

                <AuditTrailPanel entityType="document" entityId={detail.document_id} refreshKey={refreshKey} />
              </>
            ) : (
              <div className="vq-card p-10 text-center text-slate-500">Select a document.</div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display">New Document</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-semibold">Title</Label>
              <Input data-testid="doc-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" placeholder="SOP-042 — Design Change Control" /></div>
            <div><Label className="text-xs font-semibold">Type</Label>
              <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{["SOP", "Policy", "WI", "Form", "Record", "Protocol", "Report"].map((x) => (<SelectItem key={x} value={x}>{x}</SelectItem>))}</SelectContent>
              </Select></div>
            <div><Label className="text-xs font-semibold">Content</Label>
              <Textarea data-testid="doc-form-content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={10} className="mt-1.5 font-mono text-xs" placeholder="1. Purpose&#10;2. Scope&#10;3. Responsibilities&#10;4. Procedure..." /></div>
            <ComplianceCoach entityType="document" draft={form} testIdPrefix="doc-coach" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} data-testid="doc-form-submit" className="bg-slate-900 hover:bg-[#0055FF] text-white">Create Draft</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {pending && detail && (
        <ESignatureDialog
          open={esignOpen} onOpenChange={setEsignOpen}
          entityType="document" entityId={detail.document_id}
          action={`document_transition_${pending.toState}`}
          defaultMeaning={pending.toState === "Approved" || pending.toState === "Effective" ? "Approval" : "Review"}
          title={`Sign to move to ${pending.toState}`}
          onSigned={onSigned}
        />
      )}
    </DashboardLayout>
  );
}
